import { useState, useEffect } from 'react';
import { serviceService } from '../Model/Services/serviceService.js';

/**
 * ServiceViewModel Hook
 * 
 * VARFÖR VIEWMODEL?
 * - Separerar affärslogik från UI (React-komponenter)
 * - Gör det enkelt att byta datakälla (JSON → Supabase) utan att ändra UI
 * - Återanvändbar logik för både publik och admin-sida
 * 
 * VARFÖR CUSTOM HOOK?
 * - Hooks är ett React-mönster för att dela stateful logik
 * - Gör koden renare och mer testbar
 * - Kan användas i flera komponenter
 */

export const useServices = () => {
  // ===== STATE VARIABLER =====
  
  /**
   * services = lista med alla tjänster från databasen
   * setServices = funktion för att uppdatera services
   * 
   * useState([]) = börjar med tom array, fylls när data hämtas
   */
  const [services, setServices] = useState(() => serviceService.getCached() ?? []);
  
  /**
   * loading = visar om data fortfarande hämtas
   * Användning: Visa "Loading..." meddelande eller spinner i UI
   */
  const [loading, setLoading] = useState(() => !serviceService.getCached());
  
  /**
   * error = sparar eventuella fel vid datahämtning
   * Användning: Visa felmeddelande i UI om något går fel
   */
  const [error, setError] = useState(null);

  // ===== EFFECT HOOK =====
  
  /**
   * useEffect = körs när komponenten mountas (första gången)
   * [] = beroende-array (tom = körs bara en gång)
   * 
   * VAD GÖR DET?
   * - Hämtar alla tjänster från Model när komponenten laddar
   * - Uppdaterar loading och error state
   * 
   * VARFÖR?
   * - Vi vill ladda data en gång när appen startar, inte varje render
   */
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await serviceService.getAll();
        setServices(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchServices();
  }, []); // Tom array = körs bara en gång

  const refreshServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await serviceService.getAll();
      setServices(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== CRUD FUNKTIONER =====
  
  /**
   * addService = Lägger till ny tjänst
   * 
   * Användning i admin-sida:
   * const { addService } = useServices();
   * addService({ title: "Ny tjänst", image: "...", content: "..." })
   */
  const addService = async (newService) => {
    try {
      // Skickar till Model
      const created = await serviceService.create(newService);
      
      // Uppdaterar local state med ny data
      setServices([...services, created]);
      
      return created;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const uploadServiceImage = async (file) => {
    try {
      return await serviceService.uploadImage(file);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * updateService = Uppdaterar befintlig tjänst
   * 
   * Användning:
   * updateService(1, { title: "Uppdaterad tjänst" })
   */
  const updateService = async (id, updatedData) => {
    try {
      // Skickar uppdatering till Model
      const updated = await serviceService.update(id, updatedData);
      
      // Uppdaterar local state - byt den gamla tjänsten med nya
      setServices(
        services.map(service => 
          service.id === id ? updated : service
        )
      );
      
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * deleteService = Tar bort tjänst
   * 
   * Användning:
   * deleteService(1)
   */
  const deleteService = async (id) => {
    try {
      // Skickar delete-order till Model
      await serviceService.delete(id);
      
      // Uppdaterar local state - ta bort tjänsten
      setServices(
        services.filter(service => service.id !== id)
      );
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  /**
   * getServiceById = Hämtar en specifik tjänst
   * 
   * Användning:
   * const service = getServiceById(1)
   */
  const getServiceById = (id) => {
    return services.find(service => service.id === id);
  };

  // ===== RETURN =====
  
  /**
   * Vi exponerar allt som View-komponenten behöver:
   * - Data: services, loading, error
   * - Funktioner: CRUD operations
   * 
   * VARFÖR RETURN ETT OBJEKT?
   * - Gör det enkelt att destructure i komponenten:
   *   const { services, loading } = useServices();
   */
  return {
    // DATA
    services,        // Lista med alla tjänster
    loading,         // true medan data hämtas
    error,           // Felmeddelande om något går fel
    refreshServices, // Hämta om listan
    
    // FUNKTIONER
    addService,      // Lägg till ny tjänst
    updateService,   // Uppdatera tjänst
    deleteService,   // Ta bort tjänst
    getServiceById,  // Hämta en tjänst
    uploadServiceImage,
  };
};